import { useEffect, useRef, useState } from 'react';
import { ISearchStudy } from '@vivli/features/dashboard/infrastructure/interface';
import { first } from 'rxjs/operators';
import { IFilterSet, IQuickSearchApi } from '@vivli/features/search/infrastructure/interface';
import { NoSearchResultsFoundModalComponent } from '@vivli/shared/components';
import { IModalApi } from '@vivli/shared/infrastructure/interface';
import { AssignedAppTypeEnum } from '@vivli/shared/infrastructure/enum';
import { ISearchIndexApi } from '@vivli/core/infrastructure/interface';

interface IQuickSearchApiProps {
    modalApi: IModalApi;
    assignedAppType: AssignedAppTypeEnum;
    studiesSearch: ISearchIndexApi;
}

/**
 * Azure's own default page size, which this lookup inherited by sending none. Stated explicitly so
 * the backend does not have to guess what "unspecified" meant. (DEV-1133.)
 */
const QUICK_LOOKUP_PAGE_LENGTH = 50;

/**
 * Match every document and let the id filter decide, which is what this lookup means.
 *
 * DEV-1133: this fixes a live bug rather than preserving behaviour. The lookup passed no searchText
 * at all, and the old query builder turned that into the literal string "undefined" - its optional
 * chain short-circuited, and `decodeURIComponent(undefined)` returns the five-letter word, which is
 * truthy, so it was appended as the search term. Azure then required documents to contain the word
 * "undefined", which none do.
 *
 * Verified against deployed devint before changing it: for a study that exists (NCT04554901),
 * `search=undefined` with that study's own nctId filter matched 0 documents and `search=*` matched
 * 1. So quick study lookup has been returning nothing for every id and showing "No quick lookup
 * results found." every time.
 */
const QUICK_LOOKUP_SEARCH_TEXT = '*';

export const useQuickSearch = ({ modalApi, assignedAppType, studiesSearch }: IQuickSearchApiProps): IQuickSearchApi => {
    const [isShowingResults, setIsShowingResults] = useState(false);
    const [searchResults, setSearchResults] = useState<ISearchStudy[]>([]);
    const [isGettingStudies, setIsGettingStudies] = useState(false);
    const [searchText, setSearchText] = useState('');

    const filterSetsRef = useRef<IFilterSet[]>([]);

    const updateStudyData = () => {
        setIsGettingStudies(true);
        studiesSearch
            .search<ISearchStudy, any>({
                filterSets: filterSetsRef.current,
                assignedAppType,
                //DEV-1133: both of these are now stated rather than inherited, and the searchText
                //one fixes a live bug - see the note on QUICK_LOOKUP_SEARCH_TEXT.
                searchText: QUICK_LOOKUP_SEARCH_TEXT,
                top: QUICK_LOOKUP_PAGE_LENGTH
            })
            .pipe(first())
            .subscribe(({ results }) => {
                setSearchResults(results);
                setIsGettingStudies(false);
                if (results.length < 1) {
                    const modalId = modalApi.custom(
                        <NoSearchResultsFoundModalComponent
                            onClose={() => modalApi.dismiss(modalId)}
                            noneFoundMessage={'No quick lookup results found.'}
                        />,
                        {
                            showBackground: true
                        }
                    );
                }
            });
    };

    const addFilterSet = (filterSet: IFilterSet) => {
        /* clear out old failed queries or queries for a different id type */
        filterSetsRef.current = [];
        filterSetsRef.current = [...filterSetsRef.current, filterSet];
        updateStudyData();
    };

    const closeResults = () => {
        setIsShowingResults(false);
        setSearchText('');
        setSearchResults([]);
    };

    useEffect(() => {
        setIsGettingStudies(true);
        setIsShowingResults(searchResults?.length > 0);
    }, [searchResults]);

    return {
        isGettingStudies,
        isShowingResults,
        searchResults,
        addFilterSet,
        searchText,
        setSearchText,
        closeResults
    };
};
